import { useEffect, useState } from 'react';
import { Box, HStack, Text, useColorModeValue } from '@chakra-ui/react';
import { Pin } from 'lucide-react';
import { replyLabel, type ChatMessage } from './messageUtils';
import { useT } from '../../i18n';
import { shortenLinks } from '../../lib/shortLinks';

interface PinnedBannerProps {
  /** Newest first, at most MAX_PINS (D28). */
  pins: ChatMessage[];
  onJump: (message: ChatMessage) => void;
}

// Pinned messages under the chat header (M22, D28). Tapping jumps to the
// shown pin and moves on to the next, so every pin is one tap away.
export function PinnedBanner({ pins, onJump }: PinnedBannerProps) {
  const [index, setIndex] = useState(0);
  const t = useT();
  const bg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // A pin was added or removed: start again from the newest.
  const key = pins.map((pin) => pin.id).join(',');
  useEffect(() => setIndex(0), [key]);

  if (pins.length === 0) return null;
  const current = pins[Math.min(index, pins.length - 1)]!;

  return (
    <Box
      as="button"
      type="button"
      w="full"
      textAlign="left"
      px={{ base: 3, md: 4 }}
      py={2}
      bg={bg}
      borderBottomWidth="1px"
      borderColor={borderColor}
      onClick={() => {
        onJump(current);
        setIndex((i) => (i + 1) % pins.length);
      }}
      aria-label={`${t('chat.pinned.nOfTotal', { n: index + 1, total: pins.length })}: ${replyLabel(current)}`}
    >
      <HStack spacing={2} minW={0}>
        <Box color="accent" flexShrink={0}>
          <Pin size={16} />
        </Box>
        <Box flex="1" minW={0}>
          <Text fontSize="xs" fontWeight="semibold" color="accent">
            {pins.length > 1
              ? t('chat.pinned.nOfTotal', { n: index + 1, total: pins.length })
              : t('chat.pinned.one')}
          </Text>
          <Text fontSize="sm" noOfLines={1}>
            <Text as="span" fontWeight="medium">
              {current.sender?.displayName ?? t('chat.unknown')}:
            </Text>{' '}
            {shortenLinks(replyLabel(current))}
          </Text>
        </Box>
      </HStack>
    </Box>
  );
}
